import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { BeforeLeave, SiteLeave } from '@/components/siteLeave'

export interface SiteLinksOptions {
  /** The viewer is live on the stage, or going live: leaving the screen, or signing out, would end it. */
  onStage: boolean
  /** The event's own screen, `/e/<shortcode>`. A link to it or to one of its stages (`/e/<shortcode>/@handle`), such
   *  as the event's logo in the header, stays on this screen and in its room, as the stage switcher does, so it passes
   *  while live. */
  eventPath?: string
}

export interface SiteLinks {
  /**
   * SiteHeader's and SiteFooter's `beforeLeave` (the same function for the screen's life): while on the stage it holds
   * what they were about to do (follow a link, sign out, switch organisation) and the page asks first; otherwise it
   * lets it through.
   */
  beforeLeave: BeforeLeave
  /** What waits on the viewer's answer: a page to go to, signing out, or another organisation. Null when nothing does. */
  held: SiteLeave | null
  /** Drops what was held: the viewer stays (and stays live). */
  stay(): void
  /** Carries out what was held, once: goes where the link led, signs out or switches. Called once the live has ended. */
  proceed(): void
}

/** The page a path leads to, without its hash: a link within this screen (a hash on it) leaves nothing. */
const pageOf = (to: string) => {
  const url = new URL(to, window.location.origin)
  return `${url.pathname}${url.search}`
}

/** `to` is the event's own screen at `eventPath` or one of its stages (any spelling of the shortcode). */
const withinEvent = (to: string, eventPath: string | undefined) => {
  if (!eventPath) return false
  const path = new URL(to, window.location.origin).pathname.toLowerCase()
  const event = eventPath.toLowerCase()
  return path === event || path.startsWith(`${event}/`)
}

/**
 * Leaving the watch screen while live (Leaving while live), through the site's header and footer, which ask the page
 * before they leave (`beforeLeave`, components/siteLeave): the logo, the navigation and Docs, the drawer, Log in, the
 * account menu's Edit profile and Sign out, a superadmin's organisation switch, and the footer's links. They close the
 * menu or drawer used and leave clicks that open a new tab alone.
 *
 * While live (or going live), each is held: `held` says what for, and the page asks "End your live?". One thing is held
 * at a time: until the viewer answers, another leave goes nowhere and changes nothing. If the live ends by itself
 * meanwhile, what was held is carried out without asking (the reason to ask has gone). A link to this same screen (a
 * hash on it) passes, and so does one to the event's own screen or another of its stages (`eventPath`). The browser's own warning covers closing or reloading the tab; browser Back isn't held (the app
 * mounts `BrowserRouter`, and the router's `useBlocker` needs a data router: a follow-up).
 */
export function useSiteLinks({ onStage, eventPath }: SiteLinksOptions): SiteLinks {
  const location = useLocation()
  const [held, setHeld] = useState<SiteLeave | null>(null)
  // Read when the header asks, which can happen twice before a render: a second leave mustn't replace the first.
  const heldRef = useRef<SiteLeave | null>(null)
  const latest = useRef({ onStage, eventPath, here: `${location.pathname}${location.search}` })
  useLayoutEffect(() => {
    latest.current = { onStage, eventPath, here: `${location.pathname}${location.search}` }
  })

  const beforeLeave = useCallback<BeforeLeave>((leave) => {
    const { onStage: live, eventPath: event, here } = latest.current
    if (!live) return true
    if (leave.kind === 'navigate' && (pageOf(leave.to) === here || withinEvent(leave.to, event))) return true
    if (heldRef.current) return false
    heldRef.current = leave
    setHeld(leave)
    return false
  }, [])

  const stay = useCallback(() => {
    heldRef.current = null
    setHeld(null)
  }, [])
  const proceed = useCallback(() => {
    const leave = heldRef.current
    if (!leave) return
    heldRef.current = null
    setHeld(null)
    leave.proceed()
  }, [])

  // The live ended by itself (dropped, removed, the check cancelled) while the guard asked: nothing is left to end.
  useEffect(() => {
    if (!onStage && held) proceed()
  }, [onStage, held, proceed])

  return { beforeLeave, held, stay, proceed }
}
